import React from 'react';
import useSeoForPage from '../hooks/useSeoForPage';

function Tip({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mb-5">
      <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        {title}
      </h2>
      <div className="space-y-3 text-slate-600 leading-relaxed text-[15px]">{children}</div>
    </div>
  );
}

export default function GuidePage() {
  useSeoForPage('/ratgeber');
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="text-3xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        Domain-Kauf-Ratgeber
      </h1>
      <p className="text-slate-600 leading-relaxed mb-8">
        Domainpreise zu vergleichen ist gar nicht so einfach. Auf vielen Anbieter-Webseiten findet man mehr
        Sternchen als in der Milchstraße und einige Texte sind so klein, dass man sich beim Lesen die Nase am
        Bildschirm stößt. Wir zeigen Ihnen, auf welche Punkte Sie bei der Suche nach dem passenden Registrar
        unbedingt achten sollten und welche Gefahren Ihnen drohen, sobald Sie die erste Domain gekauft haben.
      </p>

      <Tip title="1. Wegzug-Gebühren: Der Transfer kann teuer werden">
        <p>
          Schlechter Service, teure Preise oder ein langsamer Kundenbereich – es gibt viele Gründe, mit seiner
          Domain den Registrar zu wechseln. Genau in dem Moment folgt aber häufig das böse Erwachen: Während einige
          Anbieter den Transfer kostenlos ermöglichen, nehmen andere Unternehmen teilweise horrende Gebühren für den
          Umzug.
        </p>
        <p>
          Durchsuchen Sie die AGBs des Anbieters oder die Bedingungen der jeweiligen Top-Level-Domain nach
          «Transfer-Out»-Gebühren oder Umzugskosten. Falls der Wegzug kostenpflichtig ist, raten wir Ihnen, sich
          einen anderen Registrar zu suchen.
        </p>
      </Tip>

      <Tip title="2. Kleingedrucktes direkt aus der Hölle">
        <p>
          Die meisten Leute lesen die viel zu langen und extrem langweiligen AGBs eines Anbieters nicht durch, bevor
          sie dort etwas kaufen. Achten Sie zumindest auf zwei Dinge:
        </p>
        <p>
          <strong>Erstens:</strong> Viele Registrare weisen oft einen Preis aus, der nur für das erste Jahr gilt.
          Sobald sich die Domain zum ersten Mal erneuert, wird häufig ein mehr als doppelt so hoher Preis fällig.
          Wenn sich hinter dem Preis einer Domain also ein Sternchen oder eine Fußnote verbirgt, gilt es, den
          dazugehörigen Text zu suchen und genau zu lesen!
        </p>
        <p>
          <strong>Zweitens:</strong> Viele Domain-Registrare fragen im Bestellprozess, ob sie während der
          Widerrufsfrist mit der Dienstleistung beginnen dürfen – sprich, die Domain vor Ablauf der 14-tägigen
          Widerrufsfrist registrieren sollen. In den AGBs wird dann darauf hingewiesen, dass in diesem Fall ein
          Werteersatz zu leisten ist, wenn man als Kunde widerruft. Hier wird man faktisch dazu gezwungen, auf sein
          Widerrufsrecht zu verzichten.
        </p>
      </Tip>

      <Tip title="3. Mehrere Jahre im Voraus zahlen">
        <p>
          Bei einigen Registraren kann man seine Domain gleich für mehrere Jahre im Voraus bezahlen und dadurch
          Geld sparen. Klingt gut? Nicht unbedingt: Zum einen ist die Ersparnis häufig kleiner als das, was man mit
          dem Geld anderweitig erwirtschaften könnte.
        </p>
        <p>
          Wichtiger aber ist folgendes: Ein Registrar registriert Ihre Domain im Hintergrund bei der jeweiligen
          Vergabestelle (bei .de-Domains zum Beispiel bei der DENIC) und erneuert sie dort jedes Jahr. Kündigen Sie
          Ihre Domain also schon nach zum Beispiel drei Jahren oder ziehen um, kann der Anbieter sich freuen – er
          hat die Vorauszahlung für die restlichen Jahre bereits kassiert. Tatsächlich gilt häufig: Viele der
          Anbieter, die eine mehrjährige Abrechnung anbieten, haben einen auffällig schlechten Service.
        </p>
      </Tip>

      <Tip title="4. Gebühren für WHOIS-Änderungen">
        <p>
          Immer wenn Sie eine Domain registrieren, werden einige Details dieser Domain, zum Beispiel der technische
          Ansprechpartner oder der Domaininhaber, in eine öffentlich zugängliche Datenbank eingetragen – die
          WHOIS-Datenbank. Eine grundlegende Aufgabe der Registrare ist es, Änderungen dieser Einträge zu
          ermöglichen. Das ist vor allem wichtig, da eine Domain gelöscht werden kann, wenn in der WHOIS-Datenbank
          keine aktuelle Adresse, E-Mail und Telefonnummer eingetragen sind.
        </p>
        <p>
          Leider gibt es immer noch viele Registrare, die für diese Änderungen Gebühren verlangen. Besitzt man viele
          Domains und ändert seinen Wohnsitz, können so schnell dreistellige Beträge fällig werden. Suchen Sie sich
          einen Domain-Anbieter, der kein Geld für WHOIS-Änderungen verlangt!
        </p>
      </Tip>

      <Tip title="5. Vorsicht bei «Privatsphäre-Schutz»-Paketen">
        <p>
          Weil die WHOIS-Daten öffentlich einsehbar sind, versuchen einige Registrare, ein
          „Privatsphäre-Schutz-Paket" zu verkaufen. Das klingt erst einmal gut – wer hat schon gerne seine private
          Adresse oder E-Mail im Netz stehen? Trotzdem wollen wir auf eine Sache hinweisen:
        </p>
        <p>
          <strong>Genau derjenige, der in der WHOIS-Datenbank als Domain-Registrant eingetragen ist, ist der
          Eigentümer der Domain.</strong> Dabei ist es egal, was in den AGBs des Privacy-Anbieters steht. Sollte es
          zu einem Disput-Verfahren kommen, werden Sie verlieren. Gerade bei großen und seriösen Anbietern ist diese
          Gefahr klein, aber generell sollte man sich dieser Situation bewusst sein, wenn man einen solchen Service
          nutzt.
        </p>
      </Tip>

      <Tip title="6. WHOIS-Crawling und Domain-Slamming">
        <p>
          Es gibt leider viele Spammer, die die WHOIS-Datenbanken durchsuchen (crawlen) und alle gefundenen
          E-Mail-Adressen in Verteiler für zweifelhafte Angebote speichern.
        </p>
        <p>
          Neben den Spammern gibt es aber noch andere Unternehmen, die sich Daten aus dem WHOIS ziehen: Registrare.
          So kann es passieren, dass man als Domain-Inhaber plötzlich eine «Rechnung» für seine Domain von einer
          unbekannten Stelle bekommt. Wird diese Rechnung bezahlt, ist a) das Geld weg und b) die Domain. Durch die
          Bezahlung wird nämlich häufig ein Domain-Transfer autorisiert. Rechtens ist das nicht, kommt aber immer
          noch vor. Vor allem bei .com-Domains sollte man vorsichtig sein.
        </p>
        <p>
          <strong>Einfacher Schutz:</strong> Legen Sie sich eine eigene E-Mail-Adresse für Domain-Angelegenheiten an
          und richten Sie Weiterleitungsregeln nur für Nachrichten von Ihrem Registrar und der Vergabestelle ein.
          Alle anderen E-Mails auf dieser Adresse sind Spam.
        </p>
      </Tip>

      <Tip title="7. Achten Sie auf das Registrar-Lock">
        <p>
          Das „Registrar-Lock" wurde erfunden, um dem Domain-Slamming ein Ende zu machen: Der Registrar bindet eine
          Domain an sich und sie kann nicht transferiert werden, solange das Schloss aktiv ist. Eigentlich eine gute
          Sache – auch wir empfehlen, dieses Schloss zu setzen.
        </p>
        <p>
          Allerdings sollte man beim Kaufen einer Domain darauf achten, ob der Anbieter automatisch ein
          Domain-Lock setzt und ob – und wie einfach – man es wieder entfernen kann. Es gibt Registrare, die horrende
          Gebühren für das Entfernen des Domain-Locks verlangen. Wir verweisen also auf Punkt 2 dieser Liste:
          Das Kleingedruckte lesen!
        </p>
        <p>
          Bei einigen Domains (.de, .com, .net, .org, .info, .biz, .cn, .us) gibt es außerdem den Auth-Code: Der
          sechs- bis sechzehnstellige Code wird zum Umzug der Domain benötigt und vom aktuellen Registrar verwaltet.
          Leider gibt es viele schwarze Schafe, die den Code nicht kostenlos herausgeben.
        </p>
      </Tip>

      <Tip title="8. Achtung vor Domain-Parking">
        <p>
          Domain-Parking ist ein Millionengeschäft: Auf geparkten Domains läuft meist nur Werbung. Normalerweise
          erscheint auf einer frisch registrierten Domain eine „Diese Domain wurde gerade registriert und ist im
          Aufbau"-Seite. Einige Domain-Anbieter – gerade die günstigsten – „parken" Ihre Domain aber per
          Voreinstellung und verdienen so bares Geld mit Werbung, solange Sie die Domain nicht nutzen.
        </p>
        <p>
          Unser Tipp: Achten Sie darauf, dass ein Registrar eine neu registrierte Domain nicht sofort mit Werbung
          plakatiert. Und wenn Sie selbst feststellen, dass Sie eine Domain aktuell nicht benötigen, parken Sie sie
          selbst – es gibt viele Anbieter, die diesen Service kostenlos zur Verfügung stellen. Besonders dreist sind
          Anbieter, die sogar noch einen monatlichen Betrag für das Parking verlangen.
        </p>
      </Tip>

      <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-5">
        <h2 className="text-lg font-semibold text-emerald-900 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Unsere Empfehlung: Der faire Domain-Anbieter
        </h2>
        <p className="text-emerald-900/80 leading-relaxed text-[15px]">
          Nochmal zusammengefasst empfehlen wir einen Domain-Anbieter, der:
        </p>
        <ul className="mt-2 space-y-1.5 text-emerald-900/80 leading-relaxed text-[15px] list-disc pl-5">
          <li>keine versteckten Gebühren erhebt</li>
          <li>standardmäßig nur eine Abrechnungsperiode berechnet</li>
          <li>die komplette Kontrolle über alle DNS-Einstellungen gewährt, sowie Zugriff auf die WHOIS-Einträge, den Auth-Code und das Registrar-Lock</li>
          <li>einen kostenlosen WHOIS-Privacy-Dienst zur Verfügung stellt und niemals persönliche Daten verkauft</li>
          <li>die eigene Domain nicht selbstständig «monetarisiert»</li>
          <li>einen erreichbaren Support bietet und nicht nur ständig versucht, zusätzliche Produkte zu verkaufen</li>
        </ul>
        <p className="mt-4 text-emerald-900/80 leading-relaxed text-[15px]">
          Umsonst ist nur der Tod: Die extrem günstigen oder kostenlosen Registrare haben meist andere Wege, ihre
          Umsätze zu generieren. Vergleichen Sie die Gesamtkosten über mehrere Jahre – genau das können Sie{' '}
          <a href="/" className="underline text-emerald-900">direkt auf unserer Startseite</a>.
        </p>
      </div>
    </div>
  );
}
